﻿import { sendDataLayer } from "../ecommerce/ecommerce";

export interface VideoData
{
    /** Unique ID of the video. */
    video_id: string;
    /** Name of the video's author or channel. May be an empty string. */
    author: string;
    /** Title of the video. */
    title: string;
}

type YTPlayer = {
    playVideo: () => void;
    pauseVideo: () => void;
    getPlayerState: () => number;
    getVideoData: () => VideoData;
    getCurrentTime(): number;
    getDuration(): number;
};

declare global
{
    interface Window
    {
        YT?: any;
        onYouTubeIframeAPIReady?: () => void;
    }
}

export class YouTubePlayer
{
    private YT = (window as any).YT;
    private container: HTMLElement;
    private videoId: string;
    private player: YTPlayer | null = null;
    private button: HTMLButtonElement;

    private static apiReady: Promise<void> | null = null;
    private playOnReady: boolean = false;

    constructor(container: HTMLElement, playOnReady: boolean)
    {
        this.container = container;
        this.playOnReady = playOnReady;

        const raw = container.dataset.video;
        if (!raw) throw new Error("data-video ontbreekt");

        const id = YouTubePlayer.extractVideoId(raw);
        if (!id) throw new Error("Ongeldige YouTube URL of ID");
        this.videoId = id;

        // Play-knop (thumbnail staat al in HTML)
        this.button = container.querySelector("button") ?? document.createElement("button");
        this.button.className = "icon-play-solid";

        this.button.addEventListener("click", () => this.loadAndPlay());
        this.container.appendChild(this.button);
    }

    private async loadAndPlay()
    {
        // API laden (eenmalig)
        await YouTubePlayer.loadAPI();

        // iframe pas nu injecteren
        this.injectPlayer();

        this.container.querySelector(".icon-play-solid").remove();
        this.container.querySelector("img").remove();
    }

    private injectPlayer()
    {
        if (this.player)
            return; // al geladen

        const iframeContainer = document.createElement("div");
        iframeContainer.className = "yt-iframe";
        this.container.appendChild(iframeContainer);

        this.player = new this.YT.Player(iframeContainer, {
            videoId: this.videoId,
            playerVars: {
                controls: 0,
                modestbranding: 1,
                rel: 0,
                fs: 0,
                iv_load_policy: 3,
                enablejsapi: 1,
            },
            events: {
                onReady: this.onPlayerReady,
                onStateChange: this.onPlayerStateChange,
                onError: this.onPlayerError
            },
        });

        // Play/pause gedrag
        this.button.onclick = () =>
        {
            if (!this.player) return;
            const state = this.player.getPlayerState();
            if (state === 1) this.player.pauseVideo();
            else this.player.playVideo();
        };
    }

    // ---------- STATIC HELPERS ----------

    private static extractVideoId(input: string): string | null
    {
        try
        {
            if (input.includes("youtube.com") || input.includes("youtu.be"))
            {
                const url = new URL(input);
                if (url.hostname.includes("youtu.be"))
                {
                    return url.pathname.replace("/", "");
                }
                return url.searchParams.get("v");
            }
            return input;
        } catch
        {
            return null;
        }
    }

    private static loadAPI(): Promise<void>
    {
        if (this.apiReady) return this.apiReady;

        this.apiReady = new Promise((resolve) =>
        {
            if (window.YT && window.YT.Player)
            {
                resolve();
                return;
            }

            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);

            window.onYouTubeIframeAPIReady = () => resolve();
        });

        return this.apiReady;
    }

    private onPlayerReady = () =>
    {
        const instance = this;
        if (this.playOnReady)
            this.player?.playVideo();
        else
        {
            $(".youtube-link").off();
            $(".youtube-link").on("click", function ()
            {
                if (instance.player.getPlayerState() != 1) // State 1 is playing
                    instance.player.playVideo();
            });
        }
    };

    private onPlayerError = (e) =>
    {
        sendDataLayer({
            event: 'error',
            eventCategory: 'youtube',
            eventAction: 'play',
            eventLabel: 'youtube: ' + e
        });
    }

    private onPlayerStateChange = (e) =>
    {
        this.player.getPlayerState() == this.YT.PlayerState.PLAYING && setTimeout(this.onPlayerPercent, 1000, e["target"]);

        var video_data = this.player.getVideoData();
        var label = video_data.video_id + ':' + video_data.title;

        if (this.player.getPlayerState() == this.YT.PlayerState.PLAYING && this.YT.gtmLastAction == "p")
        {
            sendDataLayer({
                event: 'youtube',
                eventCategory: 'youtube',
                eventAction: 'play',
                eventLabel: label
            });

            this.YT.gtmLastAction = "";
        }

        if (this.player.getPlayerState() == this.YT.PlayerState.PAUSED)
        {
            sendDataLayer({
                event: 'youtube',
                eventCategory: 'youtube',
                eventAction: 'pause',
                eventLabel: label

            });

            this.YT.gtmLastAction = "p";
        }
    }

    private onPlayerPercent = (e) =>
    {
        if (typeof (this.player) !== typeof (undefined) && typeof (this.YT) !== typeof (undefined) && this.player.getPlayerState() == this.YT.PlayerState.PLAYING)
        {
            // Set the played duration to every tenth because we'll need to also capture 90% played.
            const timestring = this.player.getDuration() - this.player.getCurrentTime() <= 1.5 ? 1 : (Math.floor(this.player.getCurrentTime() / this.player.getDuration() * 10) / 10).toFixed(2);

            let timeFloat: number = 0;

            if (typeof timestring === typeof String)
                timeFloat = parseFloat(timestring.toString());

            if (timeFloat < 0.25)
            {
                timeFloat = 0.00;
            }
            else if (timeFloat < 0.5)
            {
                timeFloat = 0.25;
            }
            else if (timeFloat < 0.75)
            {
                timeFloat = 0.50;
            }
            else if (timeFloat < 0.9)
            {
                timeFloat = 0.75;
            }
            else if (timeFloat < 1)
            {
                timeFloat = 0.90;
            }

            if (!e["lastP"] || timeFloat > e["lastP"])
            {

                var video_data = this.player.getVideoData();
                var label = video_data.video_id + ':' + video_data.title;

                e["lastP"] = timeFloat.toFixed(2);

                sendDataLayer({
                    event: "youtube",
                    eventCategory: 'youtube',
                    eventAction: timeFloat * 100 + "%",
                    eventLabel: label
                });
            }
            e["lastP"] != 1 && setTimeout(this.onPlayerPercent, 1000, e);
        }
    }
}

document.addEventListener("DOMContentLoaded", () =>
{
    reInitYoutubePlayer();
});

export const reInitYoutubePlayer = () =>
{
    document
        .querySelectorAll<HTMLElement>("[data-video]")
        .forEach((el) => new YouTubePlayer(el, true));

    document
        .querySelectorAll<HTMLElement>(".youtube-player")
        .forEach((el) => new YouTubePlayer(el, false));
};